import React, { FC } from 'react';
import { Claim, Right, Role } from 'utils/constants';
import { ClaimModel } from 'hooks/account';
import { AvailableLanguageProps } from 'components/LanguageSelect';
import { useTranslation } from 'react-i18next';

export interface Session {
    userId?: string;
    currentCompanyId?: string;
    role?: Role;
    rights: Right[];
    activeLanguage: AvailableLanguageProps;
    userCompanyRights: UserCompanyRight[];
}

export interface UserCompanyRight {
    companyId: string;
    right: Right
}

export interface SessionContextValue {
    session?: Session;
    setSession: (claims: ClaimModel[]) => Session;
    clearSession: () => void;
    hasRight: (right: Right) => boolean;
    hasRole: (role: Role) => boolean;
    hasAllRights: (rights: Right[]) => boolean;
    hasAnyRights: (rights: Right[]) => boolean;
}

export function convertToSession(claims: ClaimModel[]) {
    function claimValues(claimName: Claim) {
        const claim = claims.find((c) => c.type === claimName);
        return claim
            ? claim.values
            : undefined;
    }

    function claimFirstValue(claimName: Claim) {
        const values = claimValues(claimName);
        return values
            ? values[0]
            : undefined;
    }

    const userId = claimFirstValue(Claim.UserId);
    const role = claimFirstValue(Claim.Roles) as Role;
    const rights = (claimValues(Claim.Rights) ?? []) as Right[];
    const activeLanguage = (claimFirstValue(Claim.ActiveLanguage)) as AvailableLanguageProps;
    const currentCompanyId = claimFirstValue(Claim.CurrentCompanyId);
    const userCompanyRights = (claimValues(Claim.UserCompanyRight) ?? []).map((right) => {
        const parts = right.split(':');
        const userCompanyRight: UserCompanyRight = {
            companyId: parts[0],
            right: parts[1] as Right
        };

        return userCompanyRight;
    });

    const newSession: Session = {
        userId,
        role,
        rights,
        activeLanguage,
        currentCompanyId,
        userCompanyRights
    };

    return newSession;
}

const SessionContext = React.createContext<SessionContextValue>({
    session: undefined,
    setSession: (claims: ClaimModel[]) => {
        return convertToSession(claims);
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    clearSession: () => { },
    hasRight: (right: Right) => false,
    hasRole: (role: Role) => false,
    hasAllRights: (rights: Right[]) => false,
    hasAnyRights: (rights: Right[]) => false
});

interface SessionProviderProps {

}

export const SessionProvider: FC<SessionProviderProps> = (props) => {
    const [session, setSession] = React.useState<Session>();
    const { i18n } = useTranslation();

    const updateSession = React.useCallback((claims: ClaimModel[]) => {
        const newSession = convertToSession(claims);
        i18n.changeLanguage(newSession.activeLanguage);
        setSession(newSession);

        return newSession;
    }, [setSession]);

    const clearSession = React.useCallback(() => {
        setSession(undefined);
    }, [setSession]);

    const hasRight = React.useCallback((right: Right) => {
        if (session?.role === undefined && session?.currentCompanyId !== undefined) {
            const userCompanyRight = session?.userCompanyRights.find((userCompanyRight) =>
                userCompanyRight.companyId === session.currentCompanyId
                && userCompanyRight.right === right);

            return userCompanyRight !== undefined;
        } else if (session?.role === undefined) {
            return false;
        }

        return session?.rights?.includes(right) ?? false;
    }, [session]);

    const hasAllRights = React.useCallback((rights: Right[]) => {
        return rights.every((r) => hasRight(r));
    }, [session]);

    const hasAnyRights = React.useCallback((rights: Right[]) => {
        return rights.some((r) => hasRight(r));
    }, [session]);

    const hasRole = React.useCallback((role: Role) => {
        return session?.role === role ?? false;
    }, [session]);

    const context: SessionContextValue = React.useMemo(() => ({
        session,
        setSession: updateSession,
        clearSession,
        hasRight,
        hasRole,
        hasAllRights,
        hasAnyRights
    }), [session, updateSession, clearSession, hasRight, hasRole, hasAllRights, hasAnyRights]);

    return (
        <SessionContext.Provider value={context} {...props} />
    );
};

export const useSession = () => {
    const context = React.useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }

    return context;
};
